import { FieldNameSchema, VERIFICATION_STATUSES, type VerificationStatus } from '@clm/shared-types';

import { requireSurface } from '../routes/surfaces';

/**
 * The verification queue's URL filters — CLM-108 (R-108-17). A plain module so
 * `VerificationQueue.tsx` keeps exporting only a component (fast refresh), and
 * the parsing has a test of its own.
 *
 * The Auto-approval page links to the queue as
 * `/governance/verification?field_name=<field>&status=AutoApproved`. These are
 * **filters, never scope**: both are request parameters the API already
 * accepted (`VerificationQueueRequestSchema.field_name` and `.status`), the
 * server still derives scope from the token, and a value that is not in the
 * vocabulary is dropped here rather than sent — a 400 for a hand-edited link
 * would tell the user nothing the empty queue does not.
 */

export interface QueueUrlFilters {
  /** `''` when absent or malformed: all fields. */
  readonly fieldName: string;
  /** `null` when absent or wholly unknown: the API's default, `Pending` only. */
  readonly status: readonly VerificationStatus[] | null;
}

function isVerificationStatus(value: string): value is VerificationStatus {
  return (VERIFICATION_STATUSES as readonly string[]).includes(value);
}

/**
 * Read `field_name` and `status` from the query string. `status` may repeat
 * (`?status=AutoApproved&status=Verified`) or be comma-separated; duplicates
 * collapse and the vocabulary order is kept, so one link always sends one
 * request. `?status=Pending` alone is the default queue itself, so it reads as
 * no filter (`null`): the page then says what it always says, rather than a
 * notice calling the pending queue "not the pending queue".
 */
export function queueFiltersFromParams(params: URLSearchParams): QueueUrlFilters {
  const rawField = params.get('field_name') ?? '';
  const fieldName = FieldNameSchema.safeParse(rawField).success ? rawField : '';

  const wanted = new Set(
    params
      .getAll('status')
      .flatMap((value) => value.split(','))
      .map((value) => value.trim())
      .filter(isVerificationStatus),
  );
  const status = VERIFICATION_STATUSES.filter((value) => wanted.has(value));
  const isDefault = status.length === 0 || (status.length === 1 && status[0] === 'Pending');

  return { fieldName, status: isDefault ? null : status };
}

/**
 * How a status filter reads on the page (CLM-108, R-108-17). `onlyAutoApproved`
 * — the rows the policy settled and nothing else — is the view-only list the
 * Auto-approval page links to. `includesAutoApproved` is true of a mixed filter
 * too, whose other rows stay reviewable, so there the view-only words are
 * about the policy-settled rows alone, never the whole page.
 */
export function statusFilterView(status: readonly VerificationStatus[] | null): {
  readonly onlyAutoApproved: boolean;
  readonly includesAutoApproved: boolean;
} {
  return {
    onlyAutoApproved: status !== null && status.length > 0 && status.every((value) => value === 'AutoApproved'),
    includesAutoApproved: status?.includes('AutoApproved') === true,
  };
}

/** The queue link the Auto-approval page offers for one field's policy-settled rows (R-108-17). */
export function autoApprovedQueueHref(fieldName: string): string {
  const query = new URLSearchParams({ field_name: fieldName, status: 'AutoApproved' });
  return `${requireSurface('verify').path}?${query.toString()}`;
}
